import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE from "../utils/api";
import Modal from "./Modal";
import Tabs from "./Tabs";
import ConfirmDialog from "./ConfirmDialog";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "guardians", label: "Guardians" },
  { key: "documents", label: "Documents" },
  { key: "siblings", label: "Siblings" },
  { key: "customFields", label: "Custom Fields" },
  { key: "discounts", label: "Discounts" },
  { key: "history", label: "Academic History" },
];

const DOCUMENT_TYPES = [
  { value: "birth_certificate", label: "Birth Certificate" },
  { value: "transfer_certificate", label: "Transfer Certificate" },
  { value: "aadhar", label: "Aadhar / ID Proof" },
  { value: "report_card", label: "Report Card" },
  { value: "other", label: "Other" },
];

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  detained: "bg-amber-100 text-amber-700",
  transferred: "bg-orange-100 text-orange-700",
  graduated: "bg-indigo-100 text-indigo-700",
  alumni: "bg-purple-100 text-purple-700",
};

const InfoTile = ({ label, value, className = "" }) => (
  <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-800">{value || "—"}</p>
  </div>
);

const StudentProfileModal = ({ student, classes = [], onClose, onEdit, onStudentUpdated }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [current, setCurrent] = useState(student);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState("");

  // Documents tab state
  const [docType, setDocType] = useState("other");
  const [docTitle, setDocTitle] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Siblings tab state
  const [siblings, setSiblings] = useState([]);
  const [potentialSiblings, setPotentialSiblings] = useState([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  // Custom fields tab state
  const [fieldDefs, setFieldDefs] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [savingCustomFields, setSavingCustomFields] = useState(false);

  // Discounts tab state
  const [assignedDiscounts, setAssignedDiscounts] = useState([]);
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [discountValueOverride, setDiscountValueOverride] = useState("");
  const [discountRemarks, setDiscountRemarks] = useState("");
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [removeDiscountTarget, setRemoveDiscountTarget] = useState(null);

  // Academic history tab state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState(null); // 'detain' | 'transfer' | 'graduate'
  const [lifecycleRemarks, setLifecycleRemarks] = useState("");
  const [sectionTransferClassId, setSectionTransferClassId] = useState("");
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);

  useEffect(() => setCurrent(student), [student]);

  const notifyUpdate = useCallback(
    (updated) => {
      setCurrent(updated);
      onStudentUpdated && onStudentUpdated(updated);
    },
    [onStudentUpdated]
  );

  const refetchStudent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/students/${current._id}`);
      notifyUpdate(res.data);
    } catch (_) {
      // ignore — local state stays as-is
    }
  }, [current._id, notifyUpdate]);

  useEffect(() => {
    if (activeTab === "siblings") {
      setSiblingsLoading(true);
      Promise.all([
        axios.get(`${API_BASE}/students/${current._id}/siblings`),
        axios.get(`${API_BASE}/students/${current._id}/potential-siblings`),
      ])
        .then(([s, p]) => {
          setSiblings(s.data);
          setPotentialSiblings(p.data);
        })
        .catch(() => {})
        .finally(() => setSiblingsLoading(false));
    } else if (activeTab === "customFields") {
      axios
        .get(`${API_BASE}/custom-fields?appliesTo=student`)
        .then((res) => {
          setFieldDefs(res.data);
          setCustomValues({ ...(current.customFieldValues || {}) });
        })
        .catch(() => {});
    } else if (activeTab === "history") {
      setHistoryLoading(true);
      axios
        .get(`${API_BASE}/promotions/history/${current._id}`)
        .then((res) => setHistory(res.data))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    } else if (activeTab === "discounts") {
      setDiscountsLoading(true);
      Promise.all([
        axios.get(`${API_BASE}/students/${current._id}/discounts`),
        axios.get(`${API_BASE}/discount-definitions`),
      ])
        .then(([assigned, all]) => {
          setAssignedDiscounts(assigned.data);
          setAvailableDiscounts(all.data.filter((d) => d.isActive));
        })
        .catch(() => {})
        .finally(() => setDiscountsLoading(false));
    }
  }, [activeTab, current._id]);

  const refetchDiscounts = () => {
    axios.get(`${API_BASE}/students/${current._id}/discounts`).then((res) => setAssignedDiscounts(res.data)).catch(() => {});
  };

  const handleAssignDiscount = async () => {
    if (!selectedDiscountId) return;
    setSavingDiscount(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/students/${current._id}/discounts`, {
        discountDefinitionId: selectedDiscountId,
        valueOverride: discountValueOverride === "" ? null : Number(discountValueOverride),
        remarks: discountRemarks,
      });
      setSelectedDiscountId("");
      setDiscountValueOverride("");
      setDiscountRemarks("");
      refetchDiscounts();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to assign discount");
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!removeDiscountTarget) return;
    try {
      await axios.delete(`${API_BASE}/students/${current._id}/discounts/${removeDiscountTarget._id}`);
      setRemoveDiscountTarget(null);
      refetchDiscounts();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove discount");
      setRemoveDiscountTarget(null);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("photo", file);
      const res = await axios.post(`${API_BASE}/students/${current._id}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notifyUpdate({ ...current, photo: res.data.photo });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!docFile) return;
    setUploadingDoc(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", docFile);
      form.append("type", docType);
      if (docTitle) form.append("title", docTitle);
      await axios.post(`${API_BASE}/students/${current._id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocFile(null);
      setDocTitle("");
      await refetchStudent();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      await axios.delete(`${API_BASE}/students/${current._id}/documents/${docId}`);
      await refetchStudent();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  };

  const viewDocumentUrl = (docId) => {
    const token = localStorage.getItem("token");
    return `${API_BASE}/students/${current._id}/documents/${docId}?token=${encodeURIComponent(token || "")}`;
  };

  const handleLinkSibling = async (siblingId) => {
    setError("");
    try {
      await axios.post(`${API_BASE}/students/${current._id}/link-siblings`, { siblingStudentIds: [siblingId] });
      const [s, p] = await Promise.all([
        axios.get(`${API_BASE}/students/${current._id}/siblings`),
        axios.get(`${API_BASE}/students/${current._id}/potential-siblings`),
      ]);
      setSiblings(s.data);
      setPotentialSiblings(p.data);
      await refetchStudent();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to link sibling");
    }
  };

  const handleUnlinkSibling = async () => {
    if (!unlinkTarget) return;
    try {
      await axios.delete(`${API_BASE}/students/${unlinkTarget._id}/unlink-sibling`);
      setUnlinkTarget(null);
      const [s, p] = await Promise.all([
        axios.get(`${API_BASE}/students/${current._id}/siblings`),
        axios.get(`${API_BASE}/students/${current._id}/potential-siblings`),
      ]);
      setSiblings(s.data);
      setPotentialSiblings(p.data);
      await refetchStudent();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to unlink sibling");
      setUnlinkTarget(null);
    }
  };

  const handleSaveCustomFields = async () => {
    setSavingCustomFields(true);
    setError("");
    try {
      const res = await axios.put(`${API_BASE}/students/${current._id}/custom-fields`, { values: customValues });
      notifyUpdate(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save custom fields");
    } finally {
      setSavingCustomFields(false);
    }
  };

  const runLifecycleAction = async () => {
    setLifecycleSubmitting(true);
    setError("");
    try {
      let res;
      if (lifecycleAction === "detain") {
        res = await axios.post(`${API_BASE}/students/${current._id}/detain`, { remarks: lifecycleRemarks });
      } else if (lifecycleAction === "graduate") {
        res = await axios.post(`${API_BASE}/students/${current._id}/graduate`, { remarks: lifecycleRemarks });
      } else if (lifecycleAction === "transfer") {
        res = await axios.post(`${API_BASE}/students/${current._id}/transfer`, {
          tcReason: lifecycleRemarks,
          remarks: lifecycleRemarks,
        });
      } else if (lifecycleAction === "sectionTransfer") {
        if (!sectionTransferClassId) throw new Error("Please select a target class");
        res = await axios.post(`${API_BASE}/students/${current._id}/transfer-class`, {
          toClassId: sectionTransferClassId,
          remarks: lifecycleRemarks,
        });
      }
      notifyUpdate(res.data);
      setLifecycleAction(null);
      setLifecycleRemarks("");
      setSectionTransferClassId("");
      const h = await axios.get(`${API_BASE}/promotions/history/${current._id}`);
      setHistory(h.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Action failed");
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  return (
    <>
      <Modal title={current.name} subtitle="Student Profile" onClose={onClose} size="lg">
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="flex items-center gap-4">
            <div className="relative">
              {current.photo ? (
                <img
                  src={current.photo}
                  alt={current.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-indigo-100"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl font-bold">
                  {current.name?.charAt(0).toUpperCase() || "?"}
                </div>
              )}
              <label className="absolute -bottom-1 -right-1 bg-indigo-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs cursor-pointer hover:bg-indigo-700" title="Change photo">
                {photoUploading ? "…" : "📷"}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} disabled={photoUploading} />
              </label>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-lg font-semibold text-gray-800">{current.name}</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[current.status] || "bg-gray-100 text-gray-600"}`}>
                  {current.status ? current.status.charAt(0).toUpperCase() + current.status.slice(1) : "Active"}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {current.rollNumber} · {current.admissionNumber || "—"}
                {current.classId && ` · Class ${current.classId.className} - ${current.classId.section}`}
              </p>
            </div>
          </div>

          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          {activeTab === "profile" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <InfoTile label="Gender" value={current.gender} />
                <InfoTile
                  label="Date of Birth"
                  value={current.dateOfBirth ? new Date(current.dateOfBirth).toLocaleDateString("en-IN") : null}
                />
                <InfoTile label="Category" value={current.category} />
                <InfoTile label="Place of Birth" value={current.placeOfBirth} />
                <InfoTile label="Phone" value={current.phone} />
                <InfoTile label="Email" value={current.email} />
              </div>
              {current.address && <InfoTile label="Address" value={current.address} />}
              {current.status !== "active" && (
                <div className="border-t pt-3 grid grid-cols-2 gap-3">
                  {current.tcNumber && <InfoTile label="TC Number" value={current.tcNumber} />}
                  {current.leavingDate && (
                    <InfoTile label="Leaving Date" value={new Date(current.leavingDate).toLocaleDateString("en-IN")} />
                  )}
                  {current.tcReason && <InfoTile label="Reason" value={current.tcReason} className="col-span-2" />}
                </div>
              )}
            </div>
          )}

          {activeTab === "guardians" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {current.fatherName && <InfoTile label="Father" value={current.fatherName} />}
                {current.motherName && <InfoTile label="Mother" value={current.motherName} />}
                {current.parentPhone && <InfoTile label="Parent Phone" value={current.parentPhone} />}
                {current.parentOccupation && <InfoTile label="Parent Occupation" value={current.parentOccupation} />}
              </div>
              {current.guardians?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Additional Guardians</p>
                  {current.guardians.map((g, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {g.name} <span className="text-xs text-gray-500 capitalize">({g.relation || "guardian"})</span>
                        </p>
                        <p className="text-xs text-gray-500">{[g.phone, g.email].filter(Boolean).join(" · ")}</p>
                      </div>
                      <div className="flex gap-1">
                        {g.isPrimary && <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">Primary</span>}
                        {g.isEmergencyContact && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">Emergency</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">Edit guardians from the student's Edit form.</p>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-4">
              <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Upload a document</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select value={docType} onChange={(e) => setDocType(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Title (optional)"
                    value={docTitle}
                    onChange={(e) => setDocTitle(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <input type="file" onChange={(e) => setDocFile(e.target.files[0] || null)} className="text-sm" />
                <button
                  type="button"
                  onClick={handleUploadDocument}
                  disabled={!docFile || uploadingDoc}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {uploadingDoc ? "Uploading…" : "Upload"}
                </button>
              </div>

              <div className="space-y-2">
                {(current.documents || []).length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No documents uploaded yet.</p>
                ) : (
                  current.documents.map((doc) => (
                    <div key={doc._id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{doc.title}</p>
                        <p className="text-xs text-gray-500">
                          {DOCUMENT_TYPES.find((t) => t.value === doc.type)?.label || doc.type} ·{" "}
                          {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-IN") : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <a href={viewDocumentUrl(doc._id)} target="_blank" rel="noreferrer" className="text-indigo-600 text-sm hover:underline">
                          View
                        </a>
                        <button
                          type="button"
                          onClick={() => handleDeleteDocument(doc._id)}
                          disabled={deletingDocId === doc._id}
                          className="text-red-600 text-sm hover:underline disabled:opacity-50"
                        >
                          {deletingDocId === doc._id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === "siblings" && (
            <div className="space-y-4">
              {siblingsLoading ? (
                <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Linked Siblings</p>
                    {siblings.length === 0 ? (
                      <p className="text-sm text-gray-400">No siblings linked yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {siblings.map((s) => (
                          <div key={s._id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{s.name}</p>
                              <p className="text-xs text-gray-500">
                                {s.rollNumber} {s.classId && `· Class ${s.classId.className} - ${s.classId.section}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setUnlinkTarget(s)}
                              className="text-red-600 text-sm hover:underline"
                            >
                              Unlink
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Suggested (same parent contact)</p>
                    {potentialSiblings.length === 0 ? (
                      <p className="text-sm text-gray-400">No suggestions found.</p>
                    ) : (
                      <div className="space-y-2">
                        {potentialSiblings.map((s) => (
                          <div key={s._id} className="flex items-center justify-between bg-indigo-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{s.name}</p>
                              <p className="text-xs text-gray-500">
                                {s.rollNumber} {s.classId && `· Class ${s.classId.className} - ${s.classId.section}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleLinkSibling(s._id)}
                              className="text-indigo-600 text-sm font-medium hover:underline"
                            >
                              Link as sibling
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    Linked siblings automatically receive the school's configured tiered fee discount on future fee generation.
                  </p>
                </>
              )}
            </div>
          )}

          {activeTab === "customFields" && (
            <div className="space-y-3">
              {fieldDefs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No custom fields have been defined yet. Add some from Admin → Custom Fields.
                </p>
              ) : (
                <>
                  {fieldDefs.map((def) => (
                    <div key={def.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {def.label} {def.required && <span className="text-red-500">*</span>}
                      </label>
                      {def.fieldType === "dropdown" ? (
                        <select
                          value={customValues[def.key] || ""}
                          onChange={(e) => setCustomValues({ ...customValues, [def.key]: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Select…</option>
                          {def.options.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : def.fieldType === "checkbox" ? (
                        <input
                          type="checkbox"
                          checked={!!customValues[def.key]}
                          onChange={(e) => setCustomValues({ ...customValues, [def.key]: e.target.checked })}
                          className="h-5 w-5"
                        />
                      ) : (
                        <input
                          type={def.fieldType === "number" ? "number" : def.fieldType === "date" ? "date" : "text"}
                          value={customValues[def.key] ?? ""}
                          onChange={(e) => setCustomValues({ ...customValues, [def.key]: e.target.value })}
                          className="w-full border rounded-md px-3 py-2 text-sm"
                        />
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleSaveCustomFields}
                    disabled={savingCustomFields}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {savingCustomFields ? "Saving…" : "Save Custom Fields"}
                  </button>
                </>
              )}
            </div>
          )}

          {activeTab === "discounts" && (
            <div className="space-y-4">
              {discountsLoading ? (
                <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Assigned Discounts</p>
                    {assignedDiscounts.length === 0 ? (
                      <p className="text-sm text-gray-400">No discounts assigned yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {assignedDiscounts.map((a) => (
                          <div key={a._id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {a.discountDefinitionId?.name || "(deleted discount)"}
                                {!a.isActive && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
                              </p>
                              <p className="text-xs text-gray-500">
                                {a.discountDefinitionId && (
                                  a.valueOverride != null
                                    ? `Override: ${a.discountDefinitionId.calcMethod === "percent" ? `${a.valueOverride}%` : `Rs.${a.valueOverride}`}`
                                    : (a.discountDefinitionId.calcMethod === "percent" ? `${a.discountDefinitionId.value}%` : `Rs.${a.discountDefinitionId.value}`)
                                )}
                                {a.remarks && ` · ${a.remarks}`}
                              </p>
                            </div>
                            <button type="button" onClick={() => setRemoveDiscountTarget(a)} className="text-red-600 text-sm hover:underline">
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Assign a Discount</p>
                    <select
                      value={selectedDiscountId}
                      onChange={(e) => setSelectedDiscountId(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select discount…</option>
                      {availableDiscounts.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.name} ({d.calcMethod === "percent" ? `${d.value}%` : `Rs.${d.value}`})
                        </option>
                      ))}
                    </select>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="Override value (optional)"
                        value={discountValueOverride}
                        onChange={(e) => setDiscountValueOverride(e.target.value)}
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Remarks (optional)"
                        value={discountRemarks}
                        onChange={(e) => setDiscountRemarks(e.target.value)}
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAssignDiscount}
                      disabled={!selectedDiscountId || savingDiscount}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {savingDiscount ? "Assigning…" : "Assign Discount"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div className="space-y-4">
              {current.status === "active" && (
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setLifecycleAction("sectionTransfer")} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100">
                    Section Transfer
                  </button>
                  <button onClick={() => setLifecycleAction("detain")} className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm hover:bg-amber-100">
                    Detain
                  </button>
                  <button onClick={() => setLifecycleAction("transfer")} className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-sm hover:bg-orange-100">
                    Transfer (Leaving School)
                  </button>
                  <button onClick={() => setLifecycleAction("graduate")} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm hover:bg-indigo-100">
                    Graduate
                  </button>
                </div>
              )}

              {historyLoading ? (
                <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No academic history recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h._id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800 capitalize">{h.action.replace("_", " ")}</span>
                        <span className="text-xs text-gray-500">{new Date(h.performedAt).toLocaleDateString("en-IN")}</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {h.fromClassId ? `${h.fromClassId.className} - ${h.fromClassId.section}` : "—"}
                        {h.toClassId && ` → ${h.toClassId.className} - ${h.toClassId.section}`}
                        {h.academicYearFrom && ` · ${h.academicYearFrom}${h.academicYearTo ? ` → ${h.academicYearTo}` : ""}`}
                      </p>
                      {h.remarks && <p className="text-xs text-gray-600 mt-1">{h.remarks}</p>}
                      {h.performedBy?.name && <p className="text-xs text-gray-400 mt-1">by {h.performedBy.name}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pt-5 mt-2 border-t flex justify-end gap-2">
          <button onClick={() => onEdit(current)} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm">
            Edit Student
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm">
            Close
          </button>
        </div>
      </Modal>

      {lifecycleAction && (
        <ConfirmDialog
          title={
            lifecycleAction === "sectionTransfer" ? "Section Transfer" :
            lifecycleAction === "detain" ? "Detain Student" :
            lifecycleAction === "transfer" ? "Transfer Student (Leaving School)" :
            "Graduate Student"
          }
          message={
            <div className="space-y-3 text-left">
              {lifecycleAction === "sectionTransfer" && (
                <select
                  value={sectionTransferClassId}
                  onChange={(e) => setSectionTransferClassId(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select target class</option>
                  {classes.filter((c) => c._id !== current.classId?._id).map((c) => (
                    <option key={c._id} value={c._id}>Class {c.className} - Section {c.section}</option>
                  ))}
                </select>
              )}
              <textarea
                placeholder="Remarks (optional)"
                value={lifecycleRemarks}
                onChange={(e) => setLifecycleRemarks(e.target.value)}
                rows={2}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          }
          confirmLabel={lifecycleSubmitting ? "Working…" : "Confirm"}
          danger={lifecycleAction === "transfer" || lifecycleAction === "detain"}
          onConfirm={runLifecycleAction}
          onCancel={() => { setLifecycleAction(null); setLifecycleRemarks(""); setSectionTransferClassId(""); }}
        />
      )}

      {unlinkTarget && (
        <ConfirmDialog
          title="Unlink Sibling"
          message={`Remove ${unlinkTarget.name} from this family group? Sibling discount tiers will be recalculated.`}
          confirmLabel="Unlink"
          danger
          onConfirm={handleUnlinkSibling}
          onCancel={() => setUnlinkTarget(null)}
        />
      )}

      {removeDiscountTarget && (
        <ConfirmDialog
          title="Remove Discount"
          message={`Remove "${removeDiscountTarget.discountDefinitionId?.name || "this discount"}" from ${current.name}? It will no longer apply on future fee generation.`}
          confirmLabel="Remove"
          danger
          onConfirm={handleRemoveDiscount}
          onCancel={() => setRemoveDiscountTarget(null)}
        />
      )}
    </>
  );
};

export default StudentProfileModal;
